import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Tag, Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { getBlogArticles } from '@/lib/api';
import { useSEO, SEO_PRESETS } from '@/hooks/useSEO';

interface BlogArticle {
  id: string;
  title: string;
  preview: string;
  content: any;
  tags: string[];
  featured_image_url: string | null;
  featured_image_alt: string | null;
  author: string;
  created_at: string;
  published: boolean;
}

const BlogOverview = () => {
  // SEO für Blog-Übersicht
  useSEO(SEO_PRESETS.blog);
  
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<BlogArticle[]>([]);
  const [selectedTag, setSelectedTag] = useState('Alle');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState<string[]>(['Alle']);

  useEffect(() => {
    loadArticles();
  }, []);

  useEffect(() => {
    filterArticles();
  }, [selectedTag, searchQuery, articles]);

  const loadArticles = async () => {
    try {
      const data = await getBlogArticles();
      setArticles(data || []);
      setFilteredArticles(data || []);
      
      // Extract all unique tags
      const tags = ['Alle', ...new Set((data || []).flatMap((article: BlogArticle) => article.tags || []))];
      setAllTags(tags);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = articles;

    // Filter by tag
    if (selectedTag !== 'Alle') {
      filtered = filtered.filter(article => article.tags?.includes(selectedTag));
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        (article.preview && article.preview.toLowerCase().includes(query)) ||
        (article.tags && article.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    setFilteredArticles(filtered);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-slate-600 font-medium">Lade Blog-Artikel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header with Logo */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 shadow-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="relative">
              <img 
                src="/images/logo-klartech.png" 
                alt="Klartech Müritz Logo" 
                className="h-12 w-auto object-contain transition-transform hover:scale-105"
              />
              <div className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-green-500 animate-pulse ring-2 ring-white"></div>
            </div>
          </Link>
          <Link
            to="/"
            className="text-sm text-slate-600 hover:text-blue-600 transition-colors font-medium"
          >
            ← Zur Startseite
          </Link>
        </div>
      </header>

      {/* Hero Section with Gradient */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzAtMS4xLjktMiAyLTJzMiAuOSAyIDItLjkgMi0yIDItMi0uOS0yLTJ6bTAgMzBjMC0xLjEuOS0yIDItMnMyIC45IDIgMi0uOSAyLTIgMi0yLS45LTItMnptLTIwIDBjMC0xLjEuOS0yIDItMnMyIC45IDIgMi0uOSAyLTIgMi0yLS45LTItMnptMC0zMGMwLTEuMS45LTIgMi0yczIgLjkgMiAyLS45IDItMiAyLTItLjktMi0yeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="relative container mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Wissen & Tipps</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Klartech Blog
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              IT-Tipps, Anleitungen und Wissenswertes für Senioren, Privatleute und kleine Unternehmen aus der Region Müritz
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Search Section */}
      <section className="container mx-auto px-6 py-8 -mt-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Artikel durchsuchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedTag === tag
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:shadow-md'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          <p className="text-slate-600 mt-6 font-medium">
            {filteredArticles.length} {filteredArticles.length === 1 ? 'Artikel gefunden' : 'Artikel gefunden'}
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="container mx-auto px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📝</div>
              <p className="text-slate-600 text-lg">
                {articles.length === 0 
                  ? 'Noch keine Blog-Artikel vorhanden.' 
                  : 'Keine Artikel gefunden. Versuchen Sie eine andere Suche oder Filter.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map(article => (
                <Link
                  key={article.id}
                  to={`/blog/${article.id}`}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  {/* Featured Image */}
                  {article.featured_image_url ? (
                    <div className="relative overflow-hidden bg-gradient-to-br from-blue-100 to-slate-100" style={{ height: '200px' }}>
                      <img 
                        src={article.featured_image_url} 
                        alt={article.featured_image_alt || article.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>
                  ) : (
                    /* Gradient Fallback */
                    <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 flex items-center justify-center" style={{ height: '200px' }}>
                      <div className="text-6xl opacity-50">📰</div>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(article.tags || []).slice(0, 2).map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2 leading-tight">
                      {article.title}
                    </h3>

                    {/* Date */}
                    <div className="flex items-center text-sm text-slate-500 mb-3">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(article.created_at)}
                    </div>

                    {/* Preview */}
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4 leading-relaxed">
                      {article.preview}
                    </p>

                    {/* Read More Link */}
                    <div className="flex items-center text-blue-600 font-medium text-sm group-hover:text-blue-700">
                      Weiterlesen
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogOverview;
